import { CriteriaTypeType, CriteriaTypeZod } from 'src/api_openapi/v2/bonus-engine/schemas';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { DateTime, TimezoneType } from 'src_common/utils/time/time';

export function checkString(value: unknown): string | null {
    if (typeof value === 'string') {
        return value === '' ? null : value;
    }
    console.warn('value is not string', value, null);
    return null;
}

export function checkIsNumber(value: unknown): number | null {
    if (typeof value === 'number' && isNaN(value) === false) {
        return value;
    }

    if (typeof value === 'string') {
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue)) {
            console.warn('value is not number', value, null);
            return null;
        }
        return parsedValue;
    }
    console.warn('value is not number', value);
    return null;
}

export function checkIsArray(value: unknown): Array<string> | null {
    if (Array.isArray(value)) {
        return value.map(String);
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed.map(String);
            }
        } catch (e) {
            console.warn('Failed to parse string as array', value);
            return null;
        }
    }

    console.warn('value is not array', value);
    return null;
}

const isString = (value: unknown): value is string => {
    return typeof value === 'string';
};

const getBetResult = (input: string): string => {
    try {
        const parsed = JSON.parse(input);

        if (Array.isArray(parsed)) {
            return input;
        }
    } catch {}

    return JSON.stringify([input]);
};

export const getFieldsFromCriteria = (
    campaignModel: CampaignModel
): Partial<Record<CriteriaTypeType, string | undefined>> | undefined => {
    let transformedCriteria: Partial<Record<CriteriaTypeType, string | undefined>> | undefined;

    for (const [_key, value] of Object.entries(campaignModel.actionConditions?.criteria ?? {})) {
        let val = value.value;
        const keyIsCorrect = CriteriaTypeZod.safeParse(value.type);
        if (keyIsCorrect.success === false) {
            continue;
        }

        if (isString(val) && value.type === 'bet-result') {
            val = getBetResult(val);
        }

        const stringifiedValue = isString(val) ? val : JSON.stringify(val);

        if (transformedCriteria === undefined) {
            transformedCriteria = {
                [keyIsCorrect.data]: stringifiedValue,
            };
        } else {
            transformedCriteria[keyIsCorrect.data] = stringifiedValue;
        }
    }

    return transformedCriteria;
};

export const getFieldsFromUsageConditions = (
    campaignModel: CampaignModel
): Partial<Record<CriteriaTypeType, string>> | undefined => {
    let transformedCriteria: Partial<Record<CriteriaTypeType, string>> | undefined;
    for (const [_key, value] of Object.entries(campaignModel.bonusRewards?.[0]?.usageConditions ?? {})) {
        const keyIsCorrect = CriteriaTypeZod.safeParse(value.type);
        if (keyIsCorrect.success === false) {
            continue;
        }
        if (transformedCriteria === undefined) {
            transformedCriteria = {
                [keyIsCorrect.data]: typeof value.value === 'string' ? value.value : JSON.stringify(value.value),
            };
        } else {
            transformedCriteria[keyIsCorrect.data] =
                typeof value.value === 'string' ? value.value : JSON.stringify(value.value);
        }
    }

    return transformedCriteria;
};

export const formatNumericInput = (formInput: FormInputState<string, string>): void => {
    const value = formInput.value.trim();

    if (value === '') {
        formInput.setAsVisited();
        return;
    }

    const numericValue = parseFloat(value);

    if (isNaN(numericValue)) {
        formInput.setAsVisited();
        return;
    }

    const formattedValue = numericValue.toFixed(2);
    formInput.setValue(formattedValue);
    formInput.setAsVisited();
};

export function divideBy100(value: string): string {
    if (value.trim() === '') {
        return '';
    }
    const numericValue = parseFloat(value) / 100;
    return numericValue.toString();
}

// date formatters

export const UK_TIMEZONE: TimezoneType = 'Europe/London';

export const formDateToApiDate = (date: string): string | undefined => {
    if (date === '') {
        return undefined;
    }
    const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/; // format YYYY-MM-DDTHH:mm:ss.SSS[Z]
    if (regex.test(date) === false) {
        console.error('Form date format is invalid:', date);
        return undefined;
    }
    return date.slice(0, -5);
};

export const apiDateToFormDate = (apiDate: string | null | undefined): string => {
    if (apiDate === '' || apiDate === null || apiDate === undefined) {
        return '';
    }
    // bonus engine service sometimes sends us date in format YYYY-MM-DDTHH:mm:ss.SSSSSS
    const date = apiDate.split('.')[0] ?? '';
    const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/; // format YYYY-MM-DDTHH:mm:ss
    if (regex.test(date) === false) {
        console.error('API date format is invalid:', date);
        return '';
    }
    return `${date}.000Z`;
};

export const apiDateToDateTime = (date: string | null | undefined): DateTime | null => {
    const formDateFormat = apiDateToFormDate(date);
    if (formDateFormat === '') {
        return null;
    }
    return DateTime.from(formDateFormat)?.tz(UK_TIMEZONE) ?? null;
};
